import React, { useMemo, useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    MenuItem,
    Paper,
    Radio,
    RadioGroup,
    Select,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Typography,
    Tooltip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import HistoryIcon from '@mui/icons-material/History';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RestoreIcon from '@mui/icons-material/Restore';
import { useAllPromptsForAdmin, useTestConfigurations, useUserPrompt, useUserPromptVersions, useUserPromptBranches, useSetCurrentVersion } from '../../hooks/prompts';
import { useGetUsers } from '../../hooks/reports';
import promptsService from '../../api/promptsService';
import type { UserPrompt, CreateUserPromptDto } from '../../types/types';
import { t } from '../../i18n/t';

const statusLabel = (s: string) => {
    const map: Record<string, string> = {
        draft: t('prompts.status.draft'),
        active: t('prompts.status.active'),
        archived: t('prompts.status.archived'),
    };
    return map[s] ?? s;
};

const DashboardPrompts = () => {
    const [filterUserId, setFilterUserId] = useState<number | ''>('');
    const [filterConfigId, setFilterConfigId] = useState<number | ''>('');
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(20);
    const [createOpen, setCreateOpen] = useState(false);
    const [editingPrompt, setEditingPrompt] = useState<UserPrompt | null>(null);
    const [versionsPromptId, setVersionsPromptId] = useState<number | null>(null);
    const [migratingGlobalPrompts, setMigratingGlobalPrompts] = useState(false);

    const { data: users = [] } = useGetUsers();
    const { data: testConfigs = [], isLoading: loadingConfigs } = useTestConfigurations();
    const { data, isLoading: loadingPrompts, refetch } = useAllPromptsForAdmin(
        filterUserId === '' ? undefined : filterUserId,
        filterConfigId === '' ? undefined : filterConfigId,
        page,
        pageSize,
    );
    const prompts = data?.items ?? [];
    const total = data?.total ?? 0;

    const configById = useMemo(() => {
        const m: Record<number, string> = {};
        testConfigs.forEach((c) => { m[c.id] = c.name; });
        return m;
    }, [testConfigs]);

    const userById = useMemo(() => {
        const m: Record<number, string> = {};
        users.forEach((u: { id: number; first_name?: string; last_name?: string; email?: string }) => {
            m[u.id] = [u.last_name, u.first_name].filter(Boolean).join(' ') || u.email || `#${u.id}`;
        });
        return m;
    }, [users]);

    const formatDate = (s: string) =>
        new Date(s).toLocaleString(navigator.language, {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });

    const handleDelete = async (p: UserPrompt) => {
        if (!window.confirm(t('prompts.deleteConfirm'))) return;
        try {
            await promptsService.deleteUserPrompt(p.id, p.isGlobal ? undefined : p.userId ?? undefined);
            refetch();
        } catch (e) {
            console.error(e);
            alert(t('prompts.deleteFailed'));
        }
    };

    const handleMigrateGlobalDuplicates = async () => {
        if (!window.confirm(t('prompts.migrateGlobalDuplicatesConfirm'))) return;
        setMigratingGlobalPrompts(true);
        try {
            const result = await promptsService.migrateGlobalDuplicates();
            alert(
                t('prompts.migrateGlobalDuplicatesSuccess', {
                    globalCreated: result.globalCreated,
                    groupsFound: result.groupsFound,
                    skipped: result.skipped,
                }),
            );
            refetch();
        } catch (e) {
            console.error(e);
            alert(t('prompts.migrateGlobalDuplicatesFailed'));
        } finally {
            setMigratingGlobalPrompts(false);
        }
    };

    if (loadingConfigs) {
        return (
            <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>
                {t('prompts.title')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <Select
                    size="small"
                    displayEmpty
                    value={filterUserId}
                    onChange={(e) => {
                        setFilterUserId(e.target.value === '' ? '' : Number(e.target.value));
                        setPage(0);
                    }}
                    sx={{ minWidth: 220 }}
                >
                    <MenuItem value="">{t('prompts.allUsers')}</MenuItem>
                    {users.map((u: { id: number; first_name?: string; last_name?: string; email?: string }) => (
                        <MenuItem key={u.id} value={u.id}>
                            {[u.last_name, u.first_name].filter(Boolean).join(' ') || u.email || `User #${u.id}`}
                        </MenuItem>
                    ))}
                </Select>
                <Select
                    size="small"
                    displayEmpty
                    value={filterConfigId}
                    onChange={(e) => {
                        setFilterConfigId(e.target.value === '' ? '' : Number(e.target.value));
                        setPage(0);
                    }}
                    sx={{ minWidth: 200 }}
                >
                    <MenuItem value="">{t('prompts.allReportTypes')}</MenuItem>
                    {testConfigs.map((c) => (
                        <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                    ))}
                </Select>
                <Button variant="contained" onClick={() => setCreateOpen(true)}>
                    {t('prompts.add')}
                </Button>
                <Button
                    variant="outlined"
                    onClick={handleMigrateGlobalDuplicates}
                    disabled={migratingGlobalPrompts}
                >
                    {t('prompts.migrateGlobalDuplicates')}
                </Button>
            </Box>

            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>{t('prompts.colUser')}</TableCell>
                            <TableCell>{t('prompts.colReportType')}</TableCell>
                            <TableCell>{t('prompts.colName')}</TableCell>
                            <TableCell>{t('prompts.colStatus')}</TableCell>
                            <TableCell>{t('prompts.colUpdated')}</TableCell>
                            <TableCell align="right">{t('prompts.colActions')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loadingPrompts ? (
                            <TableRow><TableCell colSpan={6} align="center"><CircularProgress /></TableCell></TableRow>
                        ) : prompts.length === 0 ? (
                            <TableRow><TableCell colSpan={6} align="center">{t('prompts.empty')}</TableCell></TableRow>
                        ) : (
                            prompts.map((p) => (
                                <TableRow key={p.id}>
                                    <TableCell>
                                        {p.isGlobal ? t('prompts.allUsers') : userById[p.userId ?? 0] ?? `#${p.userId}`}
                                    </TableCell>
                                    <TableCell>{configById[p.testConfigurationId] ?? p.testConfigurationId}</TableCell>
                                    <TableCell>{p.name}</TableCell>
                                    <TableCell>{statusLabel(p.status)}</TableCell>
                                    <TableCell>{formatDate(p.updatedAt)}</TableCell>
                                    <TableCell align="right">
                                        <Tooltip title={t('prompts.editHint')}>
                                            <IconButton size="small" onClick={() => setEditingPrompt(p)}><EditIcon /></IconButton>
                                        </Tooltip>
                                        <Tooltip title={t('prompts.historyHint')}>
                                            <IconButton size="small" onClick={() => setVersionsPromptId(p.id)}><HistoryIcon /></IconButton>
                                        </Tooltip>
                                        <Tooltip title={t('prompts.deleteHint')}>
                                            <IconButton size="small" color="error" onClick={() => handleDelete(p)}><DeleteIcon /></IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={(_e, newPage) => setPage(newPage)}
                rowsPerPage={pageSize}
                onRowsPerPageChange={(e) => {
                    setPageSize(parseInt(e.target.value, 10));
                    setPage(0);
                }}
                rowsPerPageOptions={[10, 20, 50]}
            />

            {createOpen && (
                <CreatePromptDialog
                    users={users}
                    testConfigs={testConfigs}
                    onClose={() => setCreateOpen(false)}
                    onSaved={() => { setCreateOpen(false); refetch(); }}
                />
            )}
            {editingPrompt && (
                <EditPromptDialog
                    prompt={editingPrompt}
                    users={users}
                    testConfigs={testConfigs}
                    onClose={() => setEditingPrompt(null)}
                    onSaved={() => { setEditingPrompt(null); refetch(); }}
                />
            )}
            {versionsPromptId != null && (
                <VersionsDialogAdmin
                    promptId={versionsPromptId}
                    userId={(() => {
                        const p = prompts.find((x) => x.id === versionsPromptId);
                        return p?.isGlobal ? undefined : p?.userId ?? undefined;
                    })()}
                    onClose={() => setVersionsPromptId(null)}
                />
            )}
        </Box>
    );
};

interface CreatePromptDialogProps {
    users: Array<{ id: number; first_name?: string; last_name?: string; email?: string }>;
    testConfigs: Array<{ id: number; name: string }>;
    onClose: () => void;
    onSaved: () => void;
}

const CreatePromptDialog: React.FC<CreatePromptDialogProps> = ({ users, testConfigs, onClose, onSaved }) => {
    const [forAll, setForAll] = useState(false);
    const [userId, setUserId] = useState<number>(() => users[0]?.id ?? 0);
    React.useEffect(() => {
        if (users.length > 0 && !users.some((u) => u.id === userId)) setUserId(users[0].id);
    }, [users, userId]);
    const [name, setName] = useState('');
    const [testConfigurationId, setTestConfigurationId] = useState<number>(testConfigs[0]?.id ?? 0);
    const [status, setStatus] = useState<'draft' | 'active'>('draft');
    const [value, setValue] = useState('');
    const [description, setDescription] = useState('');
    const [saving, setSaving] = useState(false);

    const handleSave = async (asDraft: boolean) => {
        const s = asDraft ? 'draft' : 'active';
        if (!name.trim()) { alert(t('prompts.enterName')); return; }
        if (!value.trim()) { alert(t('prompts.enterText')); return; }
        setSaving(true);
        try {
            if (forAll) {
                await promptsService.createPromptForAll({
                    testConfigurationId,
                    name: name.trim(),
                    status: s,
                    value: value.trim(),
                    description: description || undefined,
                });
            } else {
                const dto: CreateUserPromptDto = {
                    userId,
                    testConfigurationId,
                    name: name.trim(),
                    status: s,
                    value: value.trim(),
                    description: description || undefined,
                };
                await promptsService.createUserPrompt(dto);
            }
            onSaved();
        } catch (e) {
            console.error(e);
            const status = (e as { response?: { status?: number } })?.response?.status;
            if (status === 409) {
                alert(t('prompts.globalAlreadyExists'));
            } else {
                alert(t('prompts.saveFailed'));
            }
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open fullWidth maxWidth="md" onClose={onClose}>
            <DialogTitle>{t('prompts.newTitle')}</DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                    <FormControlLabel
                        control={
                            <Switch checked={forAll} onChange={(e) => setForAll(e.target.checked)} />
                        }
                        label={t('prompts.forAllUsers')}
                    />
                    {!forAll && (
                        <TextField
                            select
                            label={t('prompts.user')}
                            value={userId}
                            onChange={(e) => setUserId(Number(e.target.value))}
                            size="small"
                            fullWidth
                        >
                            {users.map((u) => (
                                <MenuItem key={u.id} value={u.id}>
                                    {[u.last_name, u.first_name].filter(Boolean).join(' ') || u.email || `#${u.id}`}
                                </MenuItem>
                            ))}
                        </TextField>
                    )}
                    <TextField label={t('prompts.name')} value={name} onChange={(e) => setName(e.target.value)} size="small" fullWidth />
                    <TextField
                        select
                        label={t('prompts.reportType')}
                        value={testConfigurationId}
                        onChange={(e) => setTestConfigurationId(Number(e.target.value))}
                        size="small"
                        fullWidth
                    >
                        {testConfigs.map((c) => (
                            <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                        ))}
                    </TextField>
                    <RadioGroup row value={status} onChange={(e) => setStatus(e.target.value as 'draft' | 'active')}>
                        <FormControlLabel value="active" control={<Radio />} label={t('prompts.publishNow')} />
                        <FormControlLabel value="draft" control={<Radio />} label={t('prompts.saveAsDraft')} />
                    </RadioGroup>
                    <TextField label={t('prompts.promptText')} multiline minRows={10} value={value} onChange={(e) => setValue(e.target.value)} fullWidth />
                    <TextField label={t('prompts.note')} multiline minRows={2} value={description} onChange={(e) => setDescription(e.target.value)} fullWidth />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{t('common.cancel')}</Button>
                <Button onClick={() => handleSave(true)} disabled={saving}>{t('prompts.saveDraftBtn')}</Button>
                <Button variant="contained" onClick={() => handleSave(false)} disabled={saving}>{t('prompts.publish')}</Button>
            </DialogActions>
        </Dialog>
    );
};

interface EditPromptDialogProps {
    prompt: UserPrompt;
    users: Array<{ id: number; first_name?: string; last_name?: string; email?: string }>;
    testConfigs: Array<{ id: number; name: string }>;
    onClose: () => void;
    onSaved: () => void;
}

const EditPromptDialog: React.FC<EditPromptDialogProps> = ({ prompt, users, testConfigs, onClose, onSaved }) => {
    const [name, setName] = useState(prompt.name);
    const [status, setStatus] = useState<'draft' | 'active'>(prompt.status === 'archived' ? 'draft' : prompt.status);
    const [value, setValue] = useState('');
    const [description, setDescription] = useState('');
    const [saving, setSaving] = useState(false);

    const promptUserId = prompt.isGlobal ? undefined : prompt.userId ?? undefined;
    const { data: fullPrompt, isLoading: loadingPrompt } = useUserPrompt(prompt.id, promptUserId);
    const ownerLabel = prompt.isGlobal
        ? t('prompts.allUsers')
        : users
            .filter((u) => u.id === prompt.userId)
            .map((u) => [u.last_name, u.first_name].filter(Boolean).join(' ') || u.email || `#${u.id}`)[0] ?? `#${prompt.userId}`;

    React.useEffect(() => {
        if (fullPrompt?.versions?.length) {
            const v = fullPrompt.versions.find((x) => x.id === fullPrompt.currentVersionId) ?? fullPrompt.versions[0];
            setValue(v.value);
            setDescription(v.description ?? '');
        }
    }, [fullPrompt]);

    const handleSave = async (asDraft: boolean) => {
        const s = asDraft ? 'draft' : 'active';
        if (!name.trim()) { alert(t('prompts.enterName')); return; }
        setSaving(true);
        try {
            await promptsService.updateUserPrompt(
                prompt.id,
                { name, status: s, value: value.trim() || undefined, description: description || undefined },
                promptUserId,
            );
            onSaved();
        } catch (e) {
            console.error(e);
            alert(t('prompts.saveFailed'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open fullWidth maxWidth="md" onClose={onClose}>
            <DialogTitle>{t('prompts.editTitlePrefix')} {prompt.name}</DialogTitle>
            <DialogContent>
                {loadingPrompt ? (
                    <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <TextField label={t('prompts.name')} value={name} onChange={(e) => setName(e.target.value)} size="small" fullWidth />
                        <TextField
                            label={t('prompts.user')}
                            value={ownerLabel}
                            size="small"
                            fullWidth
                            InputProps={{ readOnly: true }}
                        />
                        <Typography variant="body2" color="text.secondary">{t('prompts.reportTypeStatic')} {testConfigs.find((c) => c.id === prompt.testConfigurationId)?.name ?? prompt.testConfigurationId}</Typography>
                        <RadioGroup row value={status} onChange={(e) => setStatus(e.target.value as 'draft' | 'active')}>
                            <FormControlLabel value="active" control={<Radio />} label={t('prompts.publishRadio')} />
                            <FormControlLabel value="draft" control={<Radio />} label={t('prompts.draftRadio')} />
                        </RadioGroup>
                        <TextField label={t('prompts.promptText')} multiline minRows={10} value={value} onChange={(e) => setValue(e.target.value)} fullWidth />
                        <TextField label={t('prompts.note')} multiline minRows={2} value={description} onChange={(e) => setDescription(e.target.value)} fullWidth />
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{t('common.cancel')}</Button>
                <Button onClick={() => handleSave(true)} disabled={saving}>{t('prompts.saveDraftBtn')}</Button>
                <Button variant="contained" onClick={() => handleSave(false)} disabled={saving}>{t('prompts.publish')}</Button>
            </DialogActions>
        </Dialog>
    );
};

interface VersionsDialogAdminProps {
    promptId: number;
    userId?: number;
    onClose: () => void;
}

const SNIPPET_LEN = 120;

const VersionsDialogAdmin: React.FC<VersionsDialogAdminProps> = ({ promptId, userId, onClose }) => {
    const { data: prompt } = useUserPrompt(promptId, userId);
    const { data: versions = [] } = useUserPromptVersions(promptId, userId);
    const { data: branches = [] } = useUserPromptBranches(promptId, userId);
    const [creatingBranch, setCreatingBranch] = useState(false);
    const promptUserId = prompt?.isGlobal ? undefined : prompt?.userId ?? userId ?? undefined;
    const setCurrentVersion = useSetCurrentVersion(promptId, promptUserId);

    const formatDate = (s: string) =>
        new Date(s).toLocaleString(navigator.language, {
            day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
        });

    const handleCreateBranch = async () => {
        const uid = prompt?.isGlobal ? undefined : prompt?.userId ?? userId;
        if (uid == null) return;
        setCreatingBranch(true);
        try {
            await promptsService.createBranch(promptId, undefined, uid);
            onClose();
        } catch (e) {
            console.error(e);
            alert(t('prompts.branchFailed'));
        } finally {
            setCreatingBranch(false);
        }
    };

    const currentVersionId = prompt?.currentVersionId ?? null;

    return (
        <Dialog open fullWidth maxWidth="md" onClose={onClose}>
            <DialogTitle>{t('prompts.versionTitle')}</DialogTitle>
            <DialogContent>
                <Typography variant="subtitle2" sx={{ mt: 1 }}>{t('prompts.savedRevisions')}</Typography>
                <TableContainer><Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>{t('prompts.when')}</TableCell>
                            <TableCell>{t('prompts.version')}</TableCell>
                            <TableCell sx={{ minWidth: 200 }}>{t('prompts.content')}</TableCell>
                            <TableCell align="center" sx={{ width: 100 }}>{t('prompts.activeCol')}</TableCell>
                            <TableCell align="center" sx={{ width: 120 }}>{t('prompts.actionsCol')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {versions.map((v) => {
                            const isActive = v.id === currentVersionId;
                            const snippet = v.value.length > SNIPPET_LEN
                                ? v.value.slice(0, SNIPPET_LEN) + '…'
                                : v.value;
                            return (
                                <TableRow key={v.id}>
                                    <TableCell>{formatDate(v.createdAt)}</TableCell>
                                    <TableCell>#{v.versionNumber}</TableCell>
                                    <TableCell>
                                        <Tooltip title={v.value || ''} placement="top-start">
                                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', maxWidth: 320 }} noWrap>
                                                {snippet || '—'}
                                            </Typography>
                                        </Tooltip>
                                        {v.description && (
                                            <Typography variant="caption" display="block" color="text.secondary">
                                                {v.description}
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell align="center">
                                        {isActive ? <CheckCircleIcon color="primary" fontSize="small" /> : '—'}
                                    </TableCell>
                                    <TableCell align="center">
                                        {!isActive && (
                                            <Tooltip title={t('prompts.makeActive')}>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => setCurrentVersion.mutate(v.id)}
                                                    disabled={setCurrentVersion.isPending}
                                                >
                                                    <RestoreIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table></TableContainer>
                <Typography variant="subtitle2" sx={{ mt: 2 }}>{t('prompts.testCopies')}</Typography>
                {branches.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">{t('prompts.noCopies')}</Typography>
                ) : (
                    <Box component="ul" sx={{ pl: 2 }}>
                        {branches.map((b) => (
                            <li key={b.id}>{b.branchName ?? b.name} — {formatDate(b.createdAt)}</li>
                        ))}
                    </Box>
                )}
                {(prompt?.userId ?? userId) != null && (
                    <Button size="small" onClick={handleCreateBranch} disabled={creatingBranch} sx={{ mt: 1 }}>
                        {creatingBranch ? t('prompts.creating') : t('prompts.createCopy')}
                    </Button>
                )}
            </DialogContent>
            <DialogActions><Button onClick={onClose}>{t('common.close')}</Button></DialogActions>
        </Dialog>
    );
};

export default DashboardPrompts;
